import { applyImpact, FULL_IMPACT } from './Impact'
import moment from "moment"

const LANGUAGE = 'fr-FR'
const CURRENCY = 'EUR'
const DATE_HUMAN_FORMAT = "DD/MM/YYYY"
const DATE_FORMAT = "YYYY-MM-DD"

export const formatCurrency = (amount_cents: number, currency=CURRENCY) => new Intl.NumberFormat(
        LANGUAGE,
        { style: "currency", currency }
    ).format(amount_cents / 100.0)

export const formatDate = (date: string) => moment(date).format(DATE_HUMAN_FORMAT)

export const parseDate = (date: string) => moment(date, DATE_HUMAN_FORMAT).format(DATE_FORMAT)

// Impact cash d'une opération : son montant, ou 0 si un autre membre du Tricount l'a payée.
export const cashCents = ({amount_cents, paid_by}: {amount_cents: number, paid_by?: string | null}) => paid_by ? 0 : amount_cents

export const computeTotalAmount = (bank_transactions: BankTransaction[]) => bank_transactions
    .reduce((acc, bankTransaction) => acc + cashCents(bankTransaction), 0)

// Impact budget : somme des opérations, chacune pour ma part, sans celles classées en Transfert.
export const computeTotalAmountWithImpact = (bank_transactions: BankTransaction[], budgets: Budget[] = []) => {
    const transferIds = budgets.filter(({transfer}) => transfer).map(({id}) => id)
    return bank_transactions
        .filter(({budget_id}) => budget_id === null || !transferIds.includes(budget_id))
        .reduce((acc, {amount_cents, impact}) => acc + applyImpact(amount_cents, impact), 0)
}

export const regularBudgets = (budgets: Budget[]) => budgets.filter(({transfer}) => !transfer)

export const generateNewBankTransaction = (account_id: number, filename: string, impact: Impact = FULL_IMPACT) => ({
    label: "",
    at: moment().format(DATE_FORMAT),
    amount_cents: 0,
    budget_id: null,
    filename: filename,
    account_id: account_id,
    impact: impact,
    is_protected: false
})

const SHORT_MONTHS = ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc']

const isOneMonth = (period: Statement['period']) => period !== null && period.start_month === period.end_month

// « Sept → Oct » et « 2026 » pour un relevé carte, « Oct » pour un relevé d'un mois, sinon son nom et
// l'année de sa dernière opération.
export const statementMonths = (statement: Statement) => statement.period
    ? isOneMonth(statement.period)
        ? SHORT_MONTHS[statement.period.start_month - 1]
        : `${SHORT_MONTHS[statement.period.start_month - 1]} → ${SHORT_MONTHS[statement.period.end_month - 1]}`
    : statement.filename.replace(/^carte\s+/i, '')

export const statementYear = (statement: Statement) => statement.period
    ? String(statement.period.year)
    : (statement.last_at ? moment(statement.last_at).format('YYYY') : '')

export const statementTitle = (statement: Statement) => statement.period
    ? isOneMonth(statement.period)
        ? `${MONTH_NAMES[statement.period.start_month - 1]} ${statement.period.year}`
        : `${statementMonths(statement)} ${statementYear(statement)}`
    : statement.filename

export const accountPath = (account: Account, filename?: Filename) => filename === undefined
    ? `/accounts/${account.id}`
    : `/accounts/${account.id}/${encodeURIComponent(filename)}`

export const accountToCategorizeCount = (account: Account) => account.statements
    .reduce((count, statement) => count + statement.to_categorize_count, 0)

const MONTH_NAMES = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
const NORMALIZED_MONTH_NAMES = MONTH_NAMES.map((name) => name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase())

// Comme StatementName.period côté API : [mois de début, mois de fin, année] d'un relevé carte ou d'un
// relevé d'un mois, quelle que soit l'orthographe (« Sept », « Septembre », « Aout »), sinon null.
export const statementPeriod = (filename: Filename): Statement['period'] => {
    const words = (filename.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().match(/[a-z]+|\d{4}/g) || [])
        .filter((word) => word !== 'carte')
    const years = words.filter((word) => /^\d{4}$/.test(word)).map(Number)
    const months = words.filter((word) => !/^\d{4}$/.test(word)).map((word) => {
        const matches = word.length < 3 ? [] : NORMALIZED_MONTH_NAMES.filter((name) => name.startsWith(word))
        return matches.length === 1 ? NORMALIZED_MONTH_NAMES.indexOf(matches[0]) + 1 : null
    })
    if(years.length === 0 || months.length < 1 || months.length > 2 || months.includes(null)) {
        return null
    }
    return { start_month: months[0] as number, end_month: months[months.length - 1] as number, year: years[years.length - 1] }
}

// Comme StatementName.debited_on côté API : « Carte Octobre / Novembre 2026 » pour le relevé débité
// en novembre 2026 (debitMonth au format YYYY-MM).
export const cardStatementName = (debitMonth: string) => {
    const end = moment(debitMonth, 'YYYY-MM'), start = end.clone().subtract(1, 'month')
    return start.year() === end.year()
        ? `Carte ${MONTH_NAMES[start.month()]} / ${MONTH_NAMES[end.month()]} ${end.year()}`
        : `Carte ${MONTH_NAMES[start.month()]} ${start.year()} / ${MONTH_NAMES[end.month()]} ${end.year()}`
}

// Compte synchronisé avec la banque (pas un Tricount ni un compte saisi à la main).
export const isBankAccount = (account: Account) => account.synced && !account.tricount

// Comme BankTransaction#from_bank? côté API : l'opération fait foi pour la date et le montant.
export const isFromBank = (bankTransaction: BankTransaction, accounts: Account[]) => {
    const account = accounts.find((account) => account.id === bankTransaction.account_id)
    return !(bankTransaction.external_id || '').startsWith('tricount:') && account !== undefined && isBankAccount(account)
}

// Soldes des Tricount : total net, et comptes triés du plus grand solde au plus petit, les soldés (0 €)
// en dernier.
export const tricountBalances = (accounts: Account[]) => {
    const tricounts = accounts.filter(({ tricount }) => tricount)
    const balance = (account: Account) => account.tricount_balance_cents || 0
    const settled = (account: Account) => balance(account) === 0
    return {
        total_cents: tricounts.reduce((sum, account) => sum + balance(account), 0),
        rows: [...tricounts].sort((a, b) => Number(settled(a)) - Number(settled(b)) || balance(b) - balance(a)),
    }
}

// Répartition égale en centimes entre `count` membres : le reste va au premier.
export const equalShares = (totalCents: number, count: number) => {
    const base = Math.floor(totalCents / count)
    return Array.from({ length: count }, (_, index) => base + (index === 0 ? totalCents - base * count : 0))
}

// Un Tricount accepte l'envoi d'une dépense : activé, en EUR, avec mon membre et un compte.
export const canSendToTricount = (tricount: Tricount) =>
    tricount.enabled && tricount.currency === 'EUR' && !!tricount.membership_uuid && tricount.account_id !== null
